<script>
    import { tick } from "svelte";

    import { preOpQuestionnaires, user } from "../../../stores";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        previewImage,
        showError,
    } from "../../../Utils/utilFunctions.svelte";

    import {
        defaultAboutMe,
        defaultPreOp,
    } from "../../../Utils/utilConstants.svelte";

    import {
        DecimalInput,
        Loader,
        PageLoading,
        RichTextEditor,
    } from "../../../Components/Common/index.svelte";

    import {
        DirectUserRoles,
        PrivateInsuranceFundCoverType,
    } from "../../../Utils/utilEnums.svelte";

    import {
        cachePreOpQuestionnaire,
        getFilteredPreOpInstructions,
    } from "../../../Utils/utilCache.svelte";

    export let userAccountId = Number(0);

    $: if (userAccountId) loadAccountDetails();

    $: isUserDoctor = $user.role == DirectUserRoles.Doctor;

    let isLoadingUserAccount = true,
        userAccount = {},
        userAccountProfile = {};

    let isUpdatingUserAccount = false;

    let accountAvatar = {},
        accountAvatarForPatient = {},
        isReplacing = false,
        isAvatarLoading = false,
        isAvatarForPatientLoading = false;

    let isLoadingPreOpQuestionnaires = false,
        filteredPreOpQuestionnaires = [],
        isLoadingPreOpInstructions = false,
        filteredPreOpInstructions = [];

    let avatarFileInput, avatarForPatientFileInput;

    let accountDefaultInsuranceFundCovers = [],
        backupAccountDefaultInsuranceFundCovers = [];

    let aboutMeRichTextEditorRef, preOpRichTextEditorRef;

    const loadAccountDetails = async () => {
        accountAvatar = {};

        isLoadingUserAccount = true;

        await apiMethods.getMethod
            .getAccountById(userAccountId)
            .then((res) => _handleAccountDetails(res))
            .catch((err) => showError(err));

        isUserDoctor &&
            (await apiMethods.getMethod
                .getAccountInsuranceDefaultCover(userAccountId)
                .then((res) => _handleAccountInsuranceDefaultCover(res))
                .catch((err) => showError(err)));

        loadPreOpQuestionnaire();
        loadPreOpInstructions();

        isAvatarLoading = true;
        isAvatarForPatientLoading = true;

        loadAccountAvatar();
        loadAccountAvatarForPatient();

        isLoadingUserAccount = false;
    };

    const loadAccountAvatar = () => {
        apiMethods.getMethod
            .getAccountAvatar(userAccountId)
            .then((res) => _handleAccountAvatar(res.results))
            .catch((err) => showError(err));
    };

    const afterAccountAvatarUpload = (res) => {
        isAvatarLoading = false;
        if (res.id) accountAvatar.id = res.id;
        else if (res.error) showError(res.error);
    };

    const loadAccountAvatarForPatient = () => {
        apiMethods.getMethod
            .getAccountAvatarForPatient(userAccountId)
            .then((res) => _handleAccountAvatarForPatient(res.results))
            .catch((err) => showError(err));
    };

    const loadPreOpQuestionnaire = async () => {
        isLoadingPreOpQuestionnaires = true;

        await cachePreOpQuestionnaire();

        await tick();

        filteredPreOpQuestionnaires = userAccount.groupId
            ? $preOpQuestionnaires.filter(
                  (questionnaire) =>
                      questionnaire.groupId == userAccount.groupId ||
                      !questionnaire.groupId,
              )
            : $preOpQuestionnaires.filter(
                  (questionnaire) => !questionnaire.groupId,
              );

        isLoadingPreOpQuestionnaires = false;
    };

    const loadPreOpInstructions = async () => {
        isLoadingPreOpInstructions = true;

        filteredPreOpInstructions =
            await getFilteredPreOpInstructions(userAccount);

        isLoadingPreOpInstructions = false;
    };

    const afterAccountAvatarForPatientUpload = (res) => {
        isAvatarForPatientLoading = false;
        if (res.id) accountAvatarForPatient.id = res.id;
        else if (res.error) showError(res.error);
    };

    const saveAccountDetails = async () => {
        isUpdatingUserAccount = true;

        userAccount = { ...userAccount, profile: { ...userAccountProfile } };

        await apiMethods.putMethod
            .updateDoctor(userAccount)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to update account details, please refresh the page and try again and contact support if the issue persists.",
                    );
                else _handleAccountDetails(res);
            })
            .catch((err) => showError(err));

        await saveDefaultPrivateInsuranceFundCovers();

        isUpdatingUserAccount = false;
    };

    const saveDefaultPrivateInsuranceFundCovers = async () => {
        try {
            let promises = [],
                hasNewFundCoverCreate = false;

            if (
                !backupAccountDefaultInsuranceFundCovers.length &&
                accountDefaultInsuranceFundCovers.length
            ) {
                promises = accountDefaultInsuranceFundCovers.map(
                    async (cover) => {
                        try {
                            const response =
                                await createDefaultInsuranceFundCover(cover);
                            hasNewFundCoverCreate = true;
                            return { success: true, data: response };
                        } catch (error) {
                            return {
                                success: false,
                                error: `Failed to create cover for insuranceId ${cover.insuranceId}: ${error.message}`,
                            };
                        }
                    },
                );
            } else if (
                accountDefaultInsuranceFundCovers.length &&
                backupAccountDefaultInsuranceFundCovers.length
            ) {
                promises = accountDefaultInsuranceFundCovers.map(
                    async (cover) => {
                        const backUp =
                            backupAccountDefaultInsuranceFundCovers.find(
                                (backup) =>
                                    backup.insuranceId == cover.insuranceId,
                            );

                        try {
                            if (!backUp) {
                                const response =
                                    await createDefaultInsuranceFundCover(
                                        cover,
                                    );
                                hasNewFundCoverCreate = true;
                                return { success: true, data: response };
                            } else if (backUp.insurance != cover.insurance) {
                                const response =
                                    await updateDefaultInsuranceFundCover(
                                        cover,
                                    );
                                return { success: true, data: response };
                            } else
                                return {
                                    success: true,
                                    data: `No update needed for insuranceId ${cover.insuranceId}`,
                                };
                        } catch (error) {
                            return {
                                success: false,
                                error: `Failed to update cover for insuranceId ${cover.insuranceId}: ${error.message}`,
                            };
                        }
                    },
                );
            }

            await Promise.all(promises);

            if (hasNewFundCoverCreate)
                await apiMethods.getMethod
                    .getAccountInsuranceDefaultCover(userAccountId)
                    .then((res) => _handleAccountInsuranceDefaultCover(res))
                    .catch((err) => showError(err));
            else
                backupAccountDefaultInsuranceFundCovers = [
                    ...accountDefaultInsuranceFundCovers,
                ];
        } catch (err) {
            showError(err);
        }
    };

    const createDefaultInsuranceFundCover = async (data) =>
        await apiMethods.postMethod.createAccountInsuranceDefaultCover(data);

    const updateDefaultInsuranceFundCover = async (data) =>
        await apiMethods.putMethod.updateAccountInsuranceDefaultCover(data);

    const onAvatarFileChange = (e) => {
        if (isAvatarLoading) return;

        const file = e.target.files[0];

        if (!file) return;

        const reader = new FileReader();

        reader.readAsDataURL(file);

        reader.onload = async (e) => {
            accountAvatar = {
                content: e.target.result,
                id: accountAvatar.id ?? null,
            };

            await updateAvatar();
        };
    };

    const onAvatarForPatientFileChange = (e) => {
        if (isAvatarForPatientLoading) return;

        const file = e.target.files[0];

        if (!file) return;

        const reader = new FileReader();

        reader.readAsDataURL(file);

        reader.onload = async (e) => {
            accountAvatarForPatient = {
                content: e.target.result,
                id: accountAvatarForPatient.id ?? null,
            };

            await updateAvatarForPatient();
        };
    };

    const updateAvatarForPatient = async () => {
        isAvatarForPatientLoading = true;

        if (isReplacing) {
            await apiMethods.putMethod
                .updateAvatar({
                    id: accountAvatarForPatient.id,
                    accountId: userAccount.id,
                    type: 3, // account avatar for patient
                    content: accountAvatarForPatient.content.split(",")[1],
                })
                .then((res) => {
                    afterAccountAvatarForPatientUpload(res);
                })
                .catch((err) => {
                    showError(err);
                    isAvatarLoading = false;
                });
        } else {
            await apiMethods.postMethod
                .createAccountAvatarForPatient({
                    accountId: userAccount.id,
                    content: accountAvatarForPatient.content.split(",")[1],
                })
                .then((res) => {
                    afterAccountAvatarForPatientUpload(res);
                })
                .catch((err) => {
                    showError(err);
                    isAvatarLoading = false;
                });
        }

        isAvatarForPatientLoading = false;
    };

    const updateAvatar = async () => {
        isAvatarLoading = true;

        if (isReplacing) {
            await apiMethods.putMethod
                .updateAvatar({
                    id: accountAvatar.id,
                    accountId: userAccount.id,
                    type: 2, // account avatar
                    content: accountAvatar.content.split(",")[1],
                })
                .then((res) => {
                    afterAccountAvatarUpload(res);
                })
                .catch((err) => {
                    showError(err);
                    isAvatarLoading = false;
                });
        } else {
            await apiMethods.postMethod
                .createAccountAvatar({
                    accountId: userAccount.id,
                    content: accountAvatar.content.split(",")[1],
                })
                .then((res) => {
                    afterAccountAvatarUpload(res);
                })
                .catch((err) => {
                    showError(err);
                    isAvatarLoading = false;
                });
        }
    };

    const handleChangePrivateInsuranceFundCoverType = (
        insuranceFundId,
        selectedType,
    ) => {
        if (
            !accountDefaultInsuranceFundCovers.some(
                (backup) => backup.insuranceId == insuranceFundId,
            )
        ) {
            accountDefaultInsuranceFundCovers.push({
                insuranceId: insuranceFundId,
                insurance: selectedType,
                accountId: Number(userAccountId),
            });
        } else
            accountDefaultInsuranceFundCovers =
                accountDefaultInsuranceFundCovers.map((cover) => {
                    if (cover.insuranceId == insuranceFundId)
                        return {
                            ...cover,
                            insurance: selectedType,
                        };
                    return cover;
                });
    };

    // HELPER FUNCTIONS
    const _handleAccountDetails = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load this account details, please try again later or contact support",
            );
        else {
            userAccount = { ...res };
            userAccountProfile = { ...res.profile };
        }
    };

    const _handleAccountInsuranceDefaultCover = (res) => {
        accountDefaultInsuranceFundCovers = [];
        backupAccountDefaultInsuranceFundCovers = [];

        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load this account default insurance funds covers, please try again later or contact support",
            );
        else {
            accountDefaultInsuranceFundCovers = [...res];
            backupAccountDefaultInsuranceFundCovers = [...res];
        }
    };

    const _handleAccountAvatar = (arr = []) => {
        isAvatarLoading = false;
        if (!arr || !arr.length) return (accountAvatar = {});

        const len = arr.length;

        accountAvatar = {
            content: `data:image/jpg;base64,${arr[len - 1].content}`,
            id: arr[len - 1].id,
        };
    };

    const _handleAccountAvatarForPatient = (arr = []) => {
        isAvatarForPatientLoading = false;
        if (!arr || !arr.length) return (accountAvatarForPatient = {});

        const len = arr.length;

        accountAvatarForPatient = {
            id: arr[len - 1].id,
            content: `data:image/jpg;base64,${arr[len - 1].content}`,
        };
    };

    const previewAvatarForPatient = () => {
        if (accountAvatarForPatient.content) {
            previewImage({
                previewTitle: "Preview Patient Avatar",
                imageContent: accountAvatarForPatient.content,
                alt: "Patient facing avatar",
            });
        }
    };

    const previewAvatar = () => {
        if (accountAvatar.content) {
            previewImage({
                previewTitle: "Preview Avatar",
                imageContent: accountAvatar.content,
                alt: "Avatar",
            });
        }
    };

    const deleteAccountAvatar = async () => {
        if (!accountAvatar.id) return;
        isAvatarLoading = true;

        await apiMethods.deleteMethod
            .deleteAccountAvatar(accountAvatar.id)
            .then((res) => {
                if (res.error) showError(res.error);
                else
                    apiMethods.getMethod
                        .getAccountAvatar(userAccountId)
                        .then((res) => _handleAccountAvatar(res.results))
                        .catch((err) => showError(err));
            })
            .catch((err) => showError(err));

        isAvatarLoading = false;
    };

    const deleteAccountAvatarForPatient = async () => {
        if (!accountAvatarForPatient.id) return;
        isAvatarForPatientLoading = true;

        await apiMethods.deleteMethod
            .deleteAccountAvatar(accountAvatarForPatient.id)
            .then((res) => {
                if (res.error) showError(res.error);
                else
                    apiMethods.getMethod
                        .getAccountAvatarForPatient(userAccountId)
                        .then((res) =>
                            _handleAccountAvatarForPatient(res.results),
                        )
                        .catch((err) => showError(err));
            })
            .catch((err) => showError(err));

        isAvatarForPatientLoading = false;
    };

    const startReplaceAccountAvatar = () => {
        isReplacing = true;
        avatarFileInput.click();
    };

    const startReplaceAccountAvatarForPatient = () => {
        isReplacing = true;
        avatarForPatientFileInput.click();
    };

    const uploadAccountAvatar = () => {
        isReplacing = false;
        avatarFileInput.click();
    };

    const uploadAccountAvatarForPatient = () => {
        isReplacing = false;
        avatarForPatientFileInput.click();
    };
</script>

{#if isLoadingUserAccount}
    <PageLoading />
{:else}
    <div class="flex flex-col flex-1 px-4 gap-y-2">
        <div
            class="flex flex-1 items-end gap-x-4 py-2 border-b border-gray-400 sticky top-0 bg-white z-10"
        >
            <div class="avatar cursor-pointer">
                {#if isAvatarLoading}
                    <div class="avatar placeholder cursor-not-allowed">
                        <div
                            class="bg-secondary text-secondary-content rounded-full w-16"
                        >
                            <Loader />
                        </div>
                    </div>
                {:else if accountAvatar.content}
                    <div
                        class="w-16 rounded-full ring ring-secondary ring-offset-base-100 ring-offset-2"
                        on:click={previewAvatar}
                    >
                        <img src={accountAvatar.content} alt="" />
                    </div>
                {:else}
                    <div
                        class="avatar placeholder cursor-pointer"
                        on:click|preventDefault={() =>
                            isAvatarLoading ? "" : uploadAccountAvatar()}
                    >
                        <div
                            class="bg-secondary text-secondary-content rounded-full w-16"
                        >
                            <span>{userAccount.fullName[0].toUpperCase()}</span>
                        </div>
                    </div>
                {/if}
            </div>
            {#if accountAvatar.content}
                <button
                    class="btn btn-ghost btn-circle btn-xs"
                    on:click|preventDefault={previewAvatar}
                >
                    <i class="fa-solid fa-magnifying-glass"></i>
                </button>
                <button
                    class="btn btn-ghost btn-circle btn-xs"
                    on:click|preventDefault={startReplaceAccountAvatar}
                >
                    <i class="fa fa-cloud-upload" aria-hidden="true"></i>
                </button>
                <button
                    class="btn btn-ghost btn-circle btn-xs"
                    on:click|preventDefault={deleteAccountAvatar}
                >
                    <i class="deleteIcon fa-solid fa-trash"></i>
                </button>
            {/if}
            <input
                type="file"
                accept=".jpg, .jpeg, .png"
                style="display: none;"
                id="avatarFileInput"
                on:change={onAvatarFileChange}
                bind:this={avatarFileInput}
            />
            <p class="font-[700] text-[18px] text-black">
                {`${userAccount.titleName} ${userAccount.fullName}`}
            </p>
        </div>
        <form
            class="flex flex-col flex-1 w-full gap-y-4"
            on:submit|preventDefault={saveAccountDetails}
        >
            <div class="flex flex-1 items-center">
                <p class="profileTitle">Registered ABN</p>
                <p class="profileContent">{userAccountProfile.abn}</p>
            </div>

            <div class="flex flex-1 items-center">
                <p class="profileTitle">AHPRA</p>
                <p class="profileContent">{userAccountProfile.ahpra}</p>
            </div>

            <div class="flex flex-1 items-center">
                <p class="profileTitle">Account Name</p>
                <p class="profileContent">
                    {userAccountProfile.bankAccountName}
                </p>
            </div>

            <div class="flex flex-1 items-center">
                <p class="profileTitle">Bank Account BSB</p>
                <p class="profileContent">
                    {userAccountProfile.bankAccountBSB}
                </p>
            </div>

            <div class="flex flex-1 items-center">
                <p class="profileTitle">Bank Account No.</p>
                <p class="profileContent">
                    {userAccountProfile.bankAccountNumber}
                </p>
            </div>

            <div class="flex flex-1 items-center">
                <p class="profileTitle">Qualifications</p>
                <input
                    type="text"
                    class="input input-bordered input-sm flex-1 w-full"
                    bind:value={userAccountProfile.qualifications}
                />
            </div>

            {#if isUserDoctor}
                {#each PrivateInsuranceFundCoverType as { id, fundName, coverTypes }}
                    <div class="flex flex-1 items-center">
                        <p class="profileTitle">{fundName}:</p>
                        <select
                            class="select select-sm select-bordered flex flex-1"
                            on:change={(e) =>
                                handleChangePrivateInsuranceFundCoverType(
                                    id,
                                    e.target.value,
                                )}
                        >
                            {#if !backupAccountDefaultInsuranceFundCovers.some((backup) => backup.insuranceId == id)}
                                <option value="" selected>None</option>
                            {/if}

                            {#each coverTypes as { value, displayName, isDefault }, index}
                                <option
                                    {value}
                                    selected={backupAccountDefaultInsuranceFundCovers.some(
                                        (backup) => backup.insuranceId == id,
                                    ) &&
                                        (accountDefaultInsuranceFundCovers.some(
                                            (cover) => cover.insurance == value,
                                        ) ||
                                            isDefault ||
                                            index == 0)}
                                >
                                    {displayName}
                                </option>
                            {/each}
                        </select>
                    </div>
                {/each}
            {/if}

            <div class="flex flex-1 items-center">
                <p class="profileTitle">Underpayment Threshold</p>

                <div class="flex flex-1">
                    <DecimalInput
                        bind:value={userAccount.underPayThreshold}
                        inputClass="w-full"
                    />
                </div>
            </div>

            <div class="flex flex-1 items-center">
                <p class="profileTitle">Pre-Op Questionnaire:</p>
                {#if isLoadingPreOpQuestionnaires}
                    <div class="flex gap-x-2">
                        <Loader />
                        <p>Loading Pre-Op Questionnaire</p>
                    </div>
                {:else if filteredPreOpQuestionnaires?.length}
                    <select
                        class="select select-sm select-bordered flex flex-1"
                        bind:value={userAccount.preOpQuestionnaire}
                    >
                        <option
                            value={Number(0)}
                            selected={!userAccount.preOpQuestionnaire}
                        >
                            None
                        </option>
                        {#each filteredPreOpQuestionnaires as { id, name }}
                            <option
                                value={id}
                                selected={userAccount.preOpQuestionnaire == id}
                            >
                                {name}
                            </option>
                        {/each}
                    </select>
                {:else}
                    <p class="profileContent">-</p>
                {/if}
            </div>

            <div class="flex flex-1 items-center">
                <p class="profileTitle">Default Pre-Op Instruction:</p>
                {#if isLoadingPreOpInstructions}
                    <div class="flex gap-x-2">
                        <Loader />
                        <p>Loading Pre-Op Instructions</p>
                    </div>
                {:else if filteredPreOpInstructions?.length}
                    <select
                        class="select select-sm select-bordered flex flex-1"
                        bind:value={userAccount.preOpInstructions}
                    >
                        <option
                            value={Number(0)}
                            selected={!userAccount.preOpInstructions}
                        >
                            None
                        </option>
                        {#each filteredPreOpInstructions as { id, name }}
                            <option
                                value={id}
                                selected={userAccount.preOpInstructions == id}
                            >
                                {name}
                            </option>
                        {/each}
                    </select>
                {:else}
                    <p class="profileContent">-</p>
                {/if}
            </div>

            <div class="flex flex-1 items-center">
                <p class="profileTitle">
                    Publish the following to the patient facing portal
                </p>
                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    bind:checked={userAccountProfile.publishProfileToPortal}
                />
            </div>

            <div class="flex flex-1 items-end">
                <p class="profileTitle h-full my-auto">
                    Patient facing profile image
                </p>

                <div class="avatar cursor-pointer">
                    {#if isAvatarForPatientLoading}
                        <div class="avatar placeholder cursor-not-allowed">
                            <div
                                class="bg-secondary text-secondary-content rounded-full w-16"
                            >
                                <Loader />
                            </div>
                        </div>
                    {:else if accountAvatarForPatient.content}
                        <div
                            class="w-16 rounded-full ring ring-secondary ring-offset-base-100 ring-offset-2"
                            on:click={previewAvatarForPatient}
                        >
                            <img src={accountAvatarForPatient.content} alt="" />
                        </div>
                    {:else}
                        <div
                            class="avatar placeholder cursor-pointer"
                            on:click|preventDefault={() =>
                                isAvatarForPatientLoading
                                    ? ""
                                    : uploadAccountAvatarForPatient()}
                        >
                            <div
                                class="bg-secondary text-secondary-content rounded-full w-16"
                            >
                                <span
                                    >{userAccount.fullName[0].toUpperCase()}</span
                                >
                            </div>
                        </div>
                    {/if}
                </div>
                {#if accountAvatarForPatient.content}
                    <button
                        class="btn btn-ghost btn-circle btn-xs ml-3"
                        on:click|preventDefault={previewAvatarForPatient}
                    >
                        <i class="fa-solid fa-magnifying-glass"></i>
                    </button>
                    <button
                        class="btn btn-ghost btn-circle btn-xs ml-3"
                        on:click|preventDefault={startReplaceAccountAvatarForPatient}
                    >
                        <i class="fa fa-cloud-upload" aria-hidden="true"></i>
                    </button>
                    <button
                        class="btn btn-ghost btn-circle btn-xs ml-3"
                        on:click|preventDefault={deleteAccountAvatarForPatient}
                    >
                        <i class="deleteIcon fa-solid fa-trash"></i>
                    </button>
                {/if}
                <input
                    type="file"
                    accept=".jpg, .jpeg, .png"
                    style="display: none;"
                    id="avatarForPatientFileInput"
                    on:change={onAvatarForPatientFileChange}
                    bind:this={avatarForPatientFileInput}
                />
            </div>

            <div class="flex flex-1 items-start mt-2">
                <p class="profileTitle">About Me</p>

                <div class="flex flex-col flex-1 items-end gap-y-2 profileContent">
                    <!-- <textarea
                        class="textarea textarea-bordered flex-1 w-full"
                        rows="3"
                        autocomplete="off"
                        placeholder={defaultAboutMe}
                        bind:value={userAccountProfile.aboutMe}
                    /> -->
                    <RichTextEditor
                        bind:content={userAccountProfile.aboutMe}
                        config={{ placeholder: defaultAboutMe }}
                        bind:this={aboutMeRichTextEditorRef}
                    />
                    <button
                        class="btn btn-ghost btn-sm w-48"
                        on:click|preventDefault={() => {
                            // userAccountProfile.aboutMe = defaultAboutMe;
                            aboutMeRichTextEditorRef.setText(defaultAboutMe);
                        }}
                    >
                        Reset to default
                    </button>
                </div>
            </div>

            <div class="flex flex-1 items-start mt-2">
                <p class="profileTitle">Pre-Op Text</p>

                <div class="flex flex-col flex-1 items-end gap-y-2 profileContent">
                    <!-- <textarea
                        class="textarea textarea-bordered flex-1 w-full"
                        rows="3"
                        autocomplete="off"
                        placeholder={defaultPreOp}
                        bind:value={userAccountProfile.preOpText}
                    /> -->
                    <RichTextEditor
                        bind:content={userAccountProfile.preOpText}
                        config={{ placeholder: defaultPreOp }}
                        bind:this={preOpRichTextEditorRef}
                    />
                    <button
                        class="btn btn-ghost btn-sm w-48"
                        on:click|preventDefault={() => {
                            // userAccountProfile.preOpText = defaultPreOp;
                            preOpRichTextEditorRef.setText(defaultPreOp);
                        }}
                    >
                        Reset to default
                    </button>
                </div>
            </div>

            <div class="flex flex-1 items-center sticky bottom-0 bg-white py-2">
                <p class="profileTitle" />
                <button
                    class={`btn btn-secondary btn-sm w-full max-w-xs ${
                        isUpdatingUserAccount ? "loading" : ""
                    }`}
                    on:click|preventDefault={saveAccountDetails}
                    disabled={isUpdatingUserAccount}
                >
                    {isUpdatingUserAccount ? "Saving" : "Save"}
                </button>
            </div>
        </form>
    </div>
{/if}

<style>
    .profileTitle,
    .profileContent {
        line-height: 2rem;
        cursor: default;
    }

    .profileTitle {
        width: 500px;
        color: gray;
    }

    .profileContent {
        width: calc(100% - 500px);
        color: black;
    }
</style>
